/**
 * FinanceHub — Phase 57 consolidation.
 *
 * Previously the Studio shell exposed 5 separate top-level tabs (Finance,
 * Sources, Purchases, Assets, Vendors) which made the nav fan out. The user
 * asked to nest these under a single "Finance" hub so the studio sidebar
 * stays compact. We render them as an inner tab strip so deep-links from the
 * dashboard / approvals inbox still work (`/studio?tab=finance` keeps
 * landing here, the inner tab carries the sub-route via ?sub=…).
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Finance from "@/pages/Finance";
import AdminSources from "@/pages/AdminSources";
import Purchases from "@/pages/Purchases";
import VendorsHub from "@/pages/VendorsHub";
import ManageRecurringTab from "@/pages/ManageRecurringTab";
import { Wallet, Landmark, Package, Factory, Repeat } from "lucide-react";

/**
 * Phase 84.35 — Assets moved out of Finance and into People (`/my-hr?tab=assets`)
 * because the register is people-centric (each row has an assignee, employees
 * declare their own device details from Profile, etc.). Purchase amounts still
 * auto-sync into Finance → Ledger via the existing asset-creation hooks, so no
 * data-flow change is required.
 */
const SUBS = [
  { id: "ledger",    label: "Ledger",    icon: Wallet,   render: () => <Finance /> },
  { id: "sources",   label: "Sources",   icon: Landmark, render: () => <AdminSources /> },
  { id: "purchases", label: "Purchases", icon: Package,  render: () => <Purchases /> },
  { id: "vendors",   label: "Vendors",   icon: Factory,  render: () => <VendorsHub /> },
  { id: "recurring", label: "Manage Recurring", icon: Repeat, render: () => <ManageRecurringTab /> },
];

export default function FinanceHub() {
  const [params, setParams] = useSearchParams();
  // ?sub=… is our sub-tab. Default to "ledger".
  const initial = params.get("sub") || "ledger";
  const [active, setActive] = useState(SUBS.find((t) => t.id === initial) ? initial : "ledger");

  // Keep the URL in sync so deep-linking and back-button work.
  useEffect(() => {
    const cur = params.get("sub");
    if (cur !== active) {
      const next = new URLSearchParams(params);
      next.set("sub", active);
      setParams(next, { replace: true });
    }
  }, [active]);

  const current = SUBS.find((t) => t.id === active) || SUBS[0];

  return (
    <div className="bg-white border-b border-slate-200">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center gap-1 overflow-x-auto">
          {SUBS.map((t) => {
            const Icon = t.icon;
            const isActive = t.id === active;
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                data-testid={`finance-sub-${t.id}`}
                className={
                  "inline-flex items-center gap-1.5 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors " +
                  (isActive
                    ? "border-[#F54800] text-[#F54800] font-semibold"
                    : "border-transparent text-slate-600 hover:text-slate-900")
                }
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="bg-[#F8F9FA]">{current.render()}</div>
    </div>
  );
}
